/**
 * STAGING-LAUNCH-READINESS-002-R2-R8-R1 — Telegram invite/share helpers + human copy.
 * Share payload is built before click; open runs synchronously in the user gesture.
 * Referral reward mutations stay OFF — never promise % bonuses here.
 * Link appears once: `url` param = deep link; `text` = message without duplicated link.
 */
import { CUSTOMER_STORE_TELEGRAM_URL } from "./external-link-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";

/** Canonical staging flag mirror — Worker REFERRAL_REWARDS_* remain false. */
export const REFERRAL_REWARDS_UI_ENABLED = false;

export type TelegramShareOpenResult = {
  ok: boolean;
  via: "openTelegramLink" | "openLink" | "window_open" | "none" | "invalid_url" | "exception";
};

export type InviteSharePayload = {
  deepLink: string;
  /** Message body for Telegram `text` param — without duplicated URL. */
  text: string;
  /** Full message for preview / copy — link once. */
  previewText: string;
  shareUrl: string;
  hasReferralToken: boolean;
};

export type InviteCardId = "gourmet" | "novelty" | "find";

export type InviteMessageCard = {
  id: InviteCardId;
  titleUk: string;
  titleEn: string;
  bodyUk: string;
  bodyEn: string;
};

/** Paid success secondary block (not the invite page). */
export function paidShareTitle(lang: StoreV2Lang): string {
  return lang === "en" ? "Liked it? Share with a friend" : "Сподобалося? Поділися з другом";
}

export function paidShareBody(lang: StoreV2Lang): string {
  return lang === "en"
    ? "Maybe someone you know would also enjoy Asian ramen, sauces, snacks or drinks. We will only send the message you choose."
    : "Можливо, комусь із твоїх друзів теж зайдуть азійські рамені, соуси, снеки чи напої. Надішлемо лише обране тобою повідомлення.";
}

export function paidShareCta(lang: StoreV2Lang): string {
  return lang === "en" ? "✈️ Invite friends" : "✈️ Запросити друзів";
}

/** Invite page chrome. */
export function invitePageTitle(lang: StoreV2Lang): string {
  return lang === "en" ? "Share “Скарби Азії”" : "Поділися “Скарбами Азії”";
}

export function invitePageBody(lang: StoreV2Lang): string {
  return lang === "en"
    ? "Choose a message — Telegram will open with the text ready. Nothing is sent without your confirmation."
    : "Обери повідомлення — Telegram відкриється з готовим текстом. Без твого підтвердження нічого не буде надіслано.";
}

export function inviteChooseLabel(lang: StoreV2Lang): string {
  return lang === "en" ? "Choose a message" : "Обери повідомлення";
}

export function inviteHowItWorksTitle(lang: StoreV2Lang): string {
  return lang === "en" ? "How inviting works" : "Як працює запрошення";
}

export function inviteHowItWorksBody(lang: StoreV2Lang): string {
  return lang === "en"
    ? "You can share the store with friends now. The bonus program is not activated yet. When rewards become available, the terms will appear here before you take part."
    : "Зараз ти можеш поділитися магазином із друзями. Бонусна програма ще не активована. Коли винагороди стануть доступними, умови з’являться тут до початку участі.";
}

/** @deprecated use invitePageTitle — kept for exit rescue until R8-R1 exit copy lands. */
export function honestInviteTitle(lang: StoreV2Lang): string {
  return invitePageTitle(lang);
}

/** @deprecated use invitePageBody */
export function honestInviteBody(lang: StoreV2Lang): string {
  return invitePageBody(lang);
}

export const INVITE_MESSAGE_CARDS: readonly InviteMessageCard[] = [
  {
    id: "gourmet",
    titleUk: "🍜 Для поціновувача азійської кухні",
    titleEn: "🍜 For Asian cuisine fans",
    bodyUk:
      "Привіт! Знайшов магазин “Скарби Азії” — тут багато справжніх азійських продуктів: рамен, соуси, снеки й напої. Мені здалося, тобі може зайти 🙂 Подивись: {referral_link}",
    bodyEn:
      "Hi! Found “Скарби Азії” — real Asian groceries: ramen, sauces, snacks and drinks. Thought you might like it 🙂 Take a look: {referral_link}",
  },
  {
    id: "novelty",
    titleUk: "🔥 Для того, хто любить новинки",
    titleEn: "🔥 For novelty seekers",
    bodyUk:
      "Ти таке любиш 😄 У “Скарбах Азії” є гострі рамені, незвичні снеки, соуси та новинки з різних країн Азії. Заглянь, раптом знайдеш щось своє: {referral_link}",
    bodyEn:
      "This is your kind of thing 😄 “Скарби Азії” has spicy ramen, unusual snacks, sauces and finds from across Asia. Take a peek: {referral_link}",
  },
  {
    id: "find",
    titleUk: "🎁 Просто поділитися знахідкою",
    titleEn: "🎁 Just sharing a find",
    bodyUk:
      "Ділюся знахідкою: “Скарби Азії” — магазин азійських продуктів прямо в Telegram. Тут зручно знайти й замовити рамен, соуси, снеки, напої та інші цікаві товари: {referral_link}",
    bodyEn:
      "Sharing a find: “Скарби Азії” — Asian groceries right in Telegram. Easy to browse and order ramen, sauces, snacks, drinks and more: {referral_link}",
  },
] as const;

export function getInviteCard(id: InviteCardId): InviteMessageCard {
  return INVITE_MESSAGE_CARDS.find((c) => c.id === id) ?? INVITE_MESSAGE_CARDS[0]!;
}

/** Message text for Telegram `text` param — strip placeholder so URL is not duplicated. */
export function messageTextWithoutLink(template: string): string {
  return template
    .replace(/\s*\{referral_link\}\s*/g, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([:.!?])/g, "$1")
    .trim();
}

export function messagePreviewWithLink(template: string, deepLink: string): string {
  return template.replace(/\{referral_link\}/g, deepLink).trim();
}

export function buildCanonicalStoreDeepLink(referralTokenOrCode?: string | null): string {
  const base = CUSTOMER_STORE_TELEGRAM_URL.replace(/\/$/, "");
  const code = String(referralTokenOrCode ?? "").trim();
  if (!code || code === "undefined" || code === "null") return base;
  // Opaque signed token / code only — never raw Telegram user id.
  if (/^\d{5,}$/.test(code)) return base;
  return `${base}?startapp=ref_${encodeURIComponent(code)}`;
}

export function resolveInviteDeepLink(input?: {
  personalReferralLink?: string | null;
  referralCode?: string | null;
  referralToken?: string | null;
}): { deepLink: string; hasReferralToken: boolean } {
  const personal = String(input?.personalReferralLink ?? "").trim();
  if (personal && /^https:\/\/t\.me\//i.test(personal) && !/undefined|null/.test(personal)) {
    return { deepLink: personal, hasReferralToken: true };
  }
  const token = String(input?.referralToken ?? input?.referralCode ?? "").trim();
  const deepLink = buildCanonicalStoreDeepLink(token || null);
  return { deepLink, hasReferralToken: Boolean(token) && deepLink.includes("startapp=ref_") };
}

export function buildTelegramShareUrl(deepLink: string, text: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(deepLink)}&text=${encodeURIComponent(text)}`;
}

export function buildInviteSharePayload(
  lang: StoreV2Lang,
  input?: {
    personalReferralLink?: string | null;
    referralCode?: string | null;
    referralToken?: string | null;
    cardId?: InviteCardId;
    /** Optional full template override (must include {referral_link} or plain text). */
    template?: string;
  },
): InviteSharePayload {
  const { deepLink, hasReferralToken } = resolveInviteDeepLink(input);
  const card = getInviteCard(input?.cardId ?? "gourmet");
  const template =
    String(input?.template ?? "").trim() || (lang === "en" ? card.bodyEn : card.bodyUk);
  const text = messageTextWithoutLink(template);
  const previewText = template.includes("{referral_link}")
    ? messagePreviewWithLink(template, deepLink)
    : `${template.trim()}\n${deepLink}`.trim();
  return {
    deepLink,
    text,
    previewText,
    shareUrl: buildTelegramShareUrl(deepLink, text),
    hasReferralToken,
  };
}

/** Default paid-screen share uses the “find” card tone once. */
export function buildPaidSharePayload(
  lang: StoreV2Lang,
  input?: {
    personalReferralLink?: string | null;
    referralCode?: string | null;
    referralToken?: string | null;
  },
): InviteSharePayload {
  return buildInviteSharePayload(lang, { ...input, cardId: "find" });
}

type TelegramWebAppShare = {
  openTelegramLink?: (url: string) => void;
  openLink?: (url: string) => void;
};

function telegramWebApp(): TelegramWebAppShare | undefined {
  return (window as unknown as { Telegram?: { WebApp?: TelegramWebAppShare } }).Telegram?.WebApp;
}

/**
 * Must run inside the click/tap stack — never after await/fetch.
 * Prefer openTelegramLink for t.me/share/url (native recipient picker).
 */
export function openTelegramShareSync(shareUrl: string): TelegramShareOpenResult {
  const value = String(shareUrl ?? "").trim();
  if (!/^https:\/\/t\.me\/share\/url\?/i.test(value)) {
    return { ok: false, via: "invalid_url" };
  }
  try {
    const tg = telegramWebApp();
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(value);
      return { ok: true, via: "openTelegramLink" };
    }
    if (tg?.openLink) {
      tg.openLink(value);
      return { ok: true, via: "openLink" };
    }
    const opened = window.open(value, "_blank", "noopener,noreferrer");
    if (opened) return { ok: true, via: "window_open" };
    return { ok: false, via: "none" };
  } catch {
    return { ok: false, via: "exception" };
  }
}
